// TODO move to tw-api-js
import type { FormDataAdapter } from "$lib/util"
import { PatchBuilder } from "$lib/util/model-adapters/patch.ts"
import {
  USA_CITIZENSHIP_TYPES,
  USA_COUNTRY_CODE,
  VISA_TYPES,
  convertDateToISOString,
  parseISODateString,
  type Customer,
} from "@tastyworks/tastyworks-api"
import {
  dateISOString,
  isFutureDate,
  isWithinMaxYears,
} from "@tastyworks/tastyworks-api/validate"
import { boolean, object, string, type InferType } from "yup"

export const citizenshipCountrySchema = object({
  citizenshipCountry: string().required(),
  birthCountry: string().when(
    "citizenshipCountry",
    ([citizenshipCountry], schema) =>
      citizenshipCountry === USA_COUNTRY_CODE
        ? schema.notRequired()
        : schema.required()
  ),
  isForeigner: boolean().test({
    name: "is-expat",
    test: (isForeigner, { parent }) => {
      return !(isForeigner && parent.citizenshipCountry === USA_COUNTRY_CODE)
    },
  }),
})

export const isPermanentResidentSchema = object({
  isPermanentResident: boolean().required(),
})

export const visaSchema = object({
  visaType: string().oneOf(Object.values(VISA_TYPES)).required(),
  visaExpirationDate: dateISOString()
    .required()
    .test(isFutureDate())
    .test(isWithinMaxYears(20)),
})

export const citizenshipStatusSchema = object({
  citizenship: citizenshipCountrySchema,
  residency: isPermanentResidentSchema,
  visa: visaSchema,
})

type CitizenshipStatusData = InferType<typeof citizenshipStatusSchema>

export const citizenshipAdapter: FormDataAdapter<
  Customer,
  CitizenshipStatusData["citizenship"]
> = {
  toForm: (customer, form) => {
    form.citizenshipCountry = customer.citizenshipCountry
    form.birthCountry = customer.birthCountry
    form.isForeigner = customer.isForeign
    return form
  },

  toModel: (form, customer) => {
    customer.citizenshipCountry = form.citizenshipCountry
    customer.birthCountry = form.birthCountry ?? ""
    return customer
  },
  toPatch: (form) =>
    new PatchBuilder<Customer>()
      .string("citizenshipCountry", form.citizenshipCountry)
      .string("birthCountry", form.birthCountry)
      .build(),
}

export const residencyAdapter: FormDataAdapter<
  Customer,
  CitizenshipStatusData["residency"]
> = {
  toForm: (customer, form) => {
    form.isPermanentResident = customer.isPermanentResident

    return form
  },
  toModel: (_, customer) => {
    // no-op
    return customer
  },
}

export const visaAdapter: FormDataAdapter<
  Customer,
  CitizenshipStatusData["visa"]
> = {
  toForm: (customer, form) => {
    form.visaType = customer.visaType ?? undefined
    form.visaExpirationDate = convertDateToISOString(
      customer.visaExpirationDate
    )

    return form
  },
  toModel: (form, customer) => {
    customer.visaType = form.visaType
    customer.visaExpirationDate = parseISODateString(form.visaExpirationDate)

    return customer
  },
  toPatch: (form) =>
    new PatchBuilder<Customer>()
      .string("visaType", form.visaType)
      .date("visaExpirationDate", parseISODateString(form.visaExpirationDate))
      .build(),
}

export function deriveUSCitizenshipType(
  formData: CitizenshipStatusData["citizenship"] &
    CitizenshipStatusData["residency"]
) {
  // NOTE: US citizens living abroad (isForeigner) cannot apply.
  if (formData.isForeigner) {
    return USA_CITIZENSHIP_TYPES.NONE
  }

  return formData.citizenshipCountry === USA_COUNTRY_CODE
    ? USA_CITIZENSHIP_TYPES.CITIZEN
    : formData.isPermanentResident
      ? USA_CITIZENSHIP_TYPES.PERMANENT_RESIDENT
      : USA_CITIZENSHIP_TYPES.NONRESIDENT_ALIEN
}

export const citizenshipTypeAdapter: FormDataAdapter<
  Customer,
  CitizenshipStatusData["citizenship"] & CitizenshipStatusData["residency"]
> = {
  toForm: (_, form) => {
    // no-op
    return form
  },
  toModel: (form, customer) => {
    customer.usaCitizenshipType = deriveUSCitizenshipType(form)
    return customer
  },
  toPatch: (form) =>
    new PatchBuilder<Customer>()
      .string("usaCitizenshipType", deriveUSCitizenshipType(form))
      .build(),
}
